import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ChevronLeft, Download, FileSpreadsheet, MapPin, FileText, Edit2, Trash2, CheckSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import { SkeletonRow } from '../components/Skeleton';
import { formatTanggalID } from '../utils/dateUtils';

const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const fmtJam = j => j ? j.slice(0, 5) : '--:--';

export default function RekapBulananPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 20;
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get('/jadwal', { params: { bulan, tahun, search, page, limit: LIMIT } })
      .then(res => { setData(res.data.data); setTotal(res.data.total); setTotalPages(res.data.totalPages); })
      .catch(() => toast.error('Gagal memuat data'))
      .finally(() => setLoading(false));
  }, [bulan, tahun, search, page]);

  useEffect(() => { setPage(1); }, [bulan, tahun, search]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t); }, [searchInput]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/jadwal/export', { params: { bulan, tahun }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `Rekap_${BULAN[bulan - 1]}_${tahun}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export berhasil!');
    } catch { toast.error('Gagal export Excel'); }
    finally { setExporting(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/jadwal/${deleteTarget.id}`);
      toast.success('Jadwal dihapus'); setDeleteTarget(null); fetchData();
    } catch { toast.error('Gagal menghapus'); }
    finally { setDeleting(false); }
  };

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate('/')}><ChevronLeft size={16} /> Kembali</button>

      <div style={{ background: 'linear-gradient(135deg, var(--purple) 0%, #9333EA 100%)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', color: 'white', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Rekap Kegiatan Bulanan</h1>
          <p style={{ opacity: 0.8, fontSize: 14, marginTop: 4 }}>Daftar seluruh kegiatan dalam satu bulan</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={bulan} onChange={e => setBulan(Number(e.target.value))} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.2)', color: 'white', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {BULAN.map((b, i) => <option key={i} value={i + 1} style={{ color: 'black' }}>{b}</option>)}
          </select>
          <select value={tahun} onChange={e => setTahun(Number(e.target.value))} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.2)', color: 'white', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {years.map(y => <option key={y} value={y} style={{ color: 'black' }}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--purple-bg)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileSpreadsheet size={18} color="var(--purple)" /></div>
            <div><div style={{ fontWeight: 700 }}>Daftar Kegiatan</div><div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{total} kegiatan ditemukan</div></div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input className="form-input" placeholder="Cari acara atau penyelenggara..." value={searchInput} onChange={e => setSearchInput(e.target.value)} style={{ width: 240, padding: '8px 12px' }} />
            <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
              {exporting ? <span className="loader" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> : <Download size={16} />} Export Excel
            </button>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>NO</th>
                <th>TANGGAL</th>
                <th>JAM</th>
                <th>NAMA ACARA</th>
                <th>PENGUNDANG</th>
                <th>LOKASI</th>
                <th>NO SURAT</th>
                <th style={{ width: 90 }}>DISPOSISI</th>
                {isAdmin && (
                  <th style={{ width: 80 }}>AKSI</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />) :
                data.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 60, textAlign: 'center', color: 'var(--gray-400)' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div><div>Tidak ada kegiatan pada periode ini</div>
                  </td></tr>
                ) : data.map((j, i) => (
                  <tr key={j.id} style={{ background: j.is_gubernur == 1 ? '#FFFBEB' : 'white' }}>
                    <td style={{ color: 'var(--gray-400)', fontWeight: 500 }}>{(page - 1) * LIMIT + i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{formatTanggalID(j.tanggal)}</div>
                      {j.is_gubernur == 1 && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', background: '#FEF3C7', color: '#92400E', borderRadius: 100, display: 'inline-block', marginTop: 2 }}>⭐ Gubernur</span>}
                    </td>
                    <td>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: 'var(--gray-600)' }}>
                        {fmtJam(j.jam_mulai)}<br />–{j.is_selesai_tbd ? 'Selesai' : fmtJam(j.jam_selesai)}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{j.nama_acara}</div>
                      {j.keterangan && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.keterangan}</div>}
                    </td>
                    <td style={{ fontSize: 13 }}>{j.penyelenggara || <span style={{ color: 'var(--gray-300)' }}>—</span>}</td>
                    <td>{j.lokasi ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}><MapPin size={11} color="var(--gray-400)" />{j.lokasi}</span> : <span style={{ color: 'var(--gray-300)' }}>—</span>}</td>
                    <td>
                      {j.no_surat ? <span style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 4 }}><FileText size={11} />{j.no_surat}</span>
                        : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                    </td>
                    <td>
                      {j.is_disposisi
                        ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--secondary)', fontWeight: 600 }}><CheckSquare size={13} />Ya</span>
                        : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px 8px' }} onClick={() => navigate(`/input?edit=${j.id}`)}><Edit2 size={13} /></button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px 8px', color: 'var(--red)' }} onClick={() => setDeleteTarget(j)}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Halaman {page} dari {totalPages} ({total} total)</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>← Sebelumnya</button>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Berikutnya →</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting}
        title="Hapus Jadwal?" message={`Jadwal "${deleteTarget?.nama_acara}" akan dihapus.`} confirmLabel="Ya, Hapus" />
    </div>
  );
}
